import { makeObservable } from "mobx";

import { BaseNodeModel } from "@/models/nodes/BaseNodeModel";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeId, NodeModel } from "@/models/nodes/types";
import type { VisitableNode, NodeVisitor } from "@/models/nodes/visitors/NodeVisitor";
import type { PlaceholderNodeProps, PlaceholderNodeConstructorProps } from "@corca/data";

export class PlaceholderNodeModel
  extends BaseNodeModel<PlaceholderNodeProps>
  implements VisitableNode
{
  static type = "placeholder" as const;

  type = PlaceholderNodeModel.type;

  protected fillRequiredProps(props: PlaceholderNodeConstructorProps): PlaceholderNodeProps {
    return props;
  }

  constructor(
    ...args: ConstructorParameters<typeof BaseNodeModel<PlaceholderNodeConstructorProps>>
  ) {
    super(...args);
    makeObservable(this, {
      inlineNode: true,
    });

    this.doc.placeholderRegistry.register(this);

    this.onCleanup(() => {
      this.doc.placeholderRegistry.unregister(this);
    });
  }

  static create(
    doc: DocumentModel,
    props: PlaceholderNodeProps = {},
    nodes: NodeModel[] = [],
    id?: NodeId,
  ) {
    return new PlaceholderNodeModel(doc, id, props, [doc.create(InlineNodeModel, nodes)]);
  }

  get linkId(): string | undefined {
    return this.props.linkId;
  }

  set linkId(linkId: string | undefined) {
    const oldLinkId = this.props.linkId;

    if (oldLinkId === linkId) {
      return;
    }

    if (oldLinkId) {
      this.doc.placeholderRegistry.unregister(this);
    }

    this.updateProps({ linkId });

    if (linkId) {
      this.doc.placeholderRegistry.register(this);
    }
  }

  get inlineNode(): InlineNodeModel | undefined {
    return this.children.find((n) => n.is(InlineNodeModel));
  }

  accept<R>(visitor: NodeVisitor<R>): R {
    return visitor.visitPlaceholderNode(this);
  }
}
